import { FC, useMemo, useRef, useState } from 'react';
import { Modal as QcsModal, useTheme } from '@mui/material';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { SectionHeader } from '../../../common/SectionHeader';
import { GridList } from '../../../common/grid/GridList';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { CellDate } from '../../../common/grid/CellDate';
import { getFirstLastNameObj, getLangNameObj, invitationCertificateIsExpired } from '../../../../utils/format';
import { QcsLoadingButton } from '../../../common/basic/QcsLoadingButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useAppSelector } from '../../../../store';
import { getMediaToken } from '../../../../utils/storage';
import { BASE_URL_WITH_VERSION, mediaApi } from '../../../../utils/api';
import { InvitedPersonsDocumentDateModal, InvitedPersonsDocumentDateModalResult } from './InvitedPersonsDocumentDateModal';
import { DocumentInputUploadModal } from '../../../risk/DocumentInputUploadModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { InnerTooltipWrapper, QcsTooltip } from '../../../common/basic/QcsTooltip';
import { selectIdentity } from '../../../../store/entities/identity';
import { hasRole } from '../../../../utils/roles';
import { setErrorSnacks } from '../../../../utils/error';
import { useNavigate } from 'react-router';
import { useInvitation } from './invitationFunctions';
import { ConfirmDialog } from '../../../common/ConfirmDialog';
import { InvitationActivityDto } from '@api/models/invitation/InvitationActivityDto';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { UserRole } from '@api/models/enums/UserRole';
import { CertificateDto } from '@api/models/suppliers/CertificateDto';
import { InvitationState } from '@api/models/enums/InvitationState';
import { InvitationDocumentResponse } from '@api/models/invitation/InvitationDocumentResponse';
import { FileUploadMediaResponse } from '@api/models/invitation/FileUploadMediaResponse';
import { invitationService } from '@api/services/services';
import { resources } from '@api/resources';

interface ActivityCertificateModalForm {
  id?: string;
  document: InvitationDocumentResponse;
  person: InvitedPersonDto;
}

interface Props {
  item?: InvitationActivityDto;
  onClose: () => void;
}

export const ActivityCertificateModal: FC<Props> = ({ item, onClose }) => {
  const { t, i18n, invitation, callWithLock, reloadInvitation, enqueueErrorSnackbar, isSaving, setIsSaving } = useInvitation();

  const [uploadModal, setUploadModal] = useState<ActivityCertificateModalForm>();
  const [dateModal, setDateModal] = useState(false);
  const [validTo, setValidTo] = useState<string>();
  const [certificateTypeId, setCertificateTypeId] = useState<string>();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const mediaToken = getMediaToken();
  const identity = useAppSelector(selectIdentity);
  const theme = useTheme();
  const navigate = useNavigate();

  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const list = useMemo(
    () =>
      ([] as ActivityCertificateModalForm[]).concat(
        ...(invitation.persons?.map((person) =>
          (
            person.documents?.filter((document) =>
              item?.activity?.certificates?.some((c: CertificateDto) => c.id === document.uploadedDocument?.id),
            ) ?? []
          ).map((document) => ({
            id: document.id,
            document,
            person,
          })),
        ) ?? []),
      ),
    [invitation.persons, item?.activity?.certificates],
  );

  const handleRenderData = (item: ActivityCertificateModalForm) => {
    const expired = !invitationCertificateIsExpired(item.document);
    const uploadDisabled =
      invitation.state === InvitationState.Archived ||
      !hasRole(identity.roles, [UserRole.ManagerWorkplace, UserRole.ManagerOsah, UserRole.ExternalManager]);
    const downloadDisabled = !item.document.uploadedDocument;
    const approveDisabled = !hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.ManagerWorkplace]) || !item.document.uploadedDocument || expired;
    const alreadyApproved = !!item.document.approvedOn;
    const deleteDisabled = !item.document.uploadedDocument || invitation.state === InvitationState.Archived;

    const handleRowClick = () => {
      if (
        hasRole(identity.roles, [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist])
      ) {
        navigate(`/supplier/${invitation.supplier?.id}/employee/${item.person.employeeId}`, {
          state: { back: `/invitation/${invitation.id}/activity` },
        });
      } else if (hasRole(identity.roles, [UserRole.ExternalManager])) {
        navigate(`/supplier/employee/${item.person.employeeId}`, {
          state: { back: `/invitation/${invitation.id}/activity` },
        });
      }
    };

    return (
      <>
        <QcsTableCell onClick={handleRowClick}>{getFirstLastNameObj(item.person)}</QcsTableCell>
        <QcsTableCell>{getLangNameObj(i18n, item.document.requiredCertificate)}</QcsTableCell>
        <CellDate
          sx={{
            color: expired ? theme.palette.error.main : undefined,
          }}
          value={item.document.validTo}
        />
        <QcsTableCell align="right">
          <QcsTooltip title={`${t('invitation.tabs.tooltips.uploadCertificate')} ${t('common.fileMaxSize')}`} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                disabled={uploadDisabled}
                onClick={handleUpload(item)}
                loading={isSaving}
                resourceLoading={[resources.approveDocumentCertificate]}
              >
                <CloudUploadIcon color={uploadDisabled ? undefined : !!item.document.uploadedDocument && !expired ? 'success' : 'warning'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>

          <QcsTooltip title={t('invitation.tabs.tooltips.downloadCertififace')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                onClick={handleDownload(item)}
                disabled={downloadDisabled}
                loading={isSaving}
                resourceLoading={[resources.approveDocumentCertificate]}
              >
                <CloudDownloadIcon color={downloadDisabled ? undefined : 'success'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>

          <QcsTooltip title={t('invitation.tabs.tooltips.approveCertificate')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                onClick={handleApprove(item)}
                disabled={approveDisabled || alreadyApproved}
                loading={isSaving}
                resourceLoading={[resources.approveDocumentCertificate]}
              >
                <CheckCircleIcon color={approveDisabled ? undefined : alreadyApproved ? 'success' : 'warning'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>

          <QcsTooltip title={t('invitation.tabs.tooltips.deleteCertificate')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                onClick={() => setShowConfirmModal(true)}
                disabled={deleteDisabled}
                loading={isSaving}
                resourceLoading={[resources.approveDocumentCertificate]}
              >
                <DeleteForeverIcon color={deleteDisabled ? undefined : 'error'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>
        </QcsTableCell>
        <ConfirmDialog
          onClose={() => setShowConfirmModal(false)}
          onConfirm={showConfirmModal ? handleDelete(item) : null}
          title={t('document.certificateDeleteDialog.title')}
          message={t('document.certificateDeleteDialog.message')}
        />
      </>
    );
  };

  const handleUpload = (item: ActivityCertificateModalForm) => () => {
    setUploadModal(item);

    if (item.document.requiredCertificate?.expiryDateRequired || (item.document.requiredCertificate?.types?.length ?? 0) > 0) {
      setDateModal(true);
    } else {
      setValidTo(undefined);
      setCertificateTypeId(undefined);
      fileRef.current?.click();
    }
  };

  const handleDateModalClose = (result?: InvitedPersonsDocumentDateModalResult) => {
    setValidTo(result?.date);
    setCertificateTypeId(result?.certificateTypeId);
    setDateModal(false);

    if (result) {
      fileRef.current?.click();
    }
  };

  const handleFileChange = (files: FileList | null) => {
    if (!fileRef.current || !files) {
      return;
    }

    const file = files[0];
    setSelectedFile(file);
    fileRef.current.value = '';
  };

  const handleFileUploaded = async (document: FileUploadMediaResponse) => {
    if (!selectedFile || !uploadModal) {
      return;
    }

    await callWithLock(async () => {
      await invitationService.updateDocument(invitation.id!, uploadModal.document.id!, {
        document: document.id!,
        validTo,
        personId: uploadModal.person.id!,
        certificateTypeId,
      });
      await reloadInvitation();
    });

    setSelectedFile(null);
  };

  const handleFileCancel = () => {
    setSelectedFile(null);
  };

  const handleDownload = (item: ActivityCertificateModalForm) => () => {
    const url = `${BASE_URL_WITH_VERSION}${item.document?.uploadedDocument?.path}?token=${mediaToken}`;
    const link = document.createElement('a');
    link.href = url;
    // Taget for no "same-origin" - some browser can block download and redirect to file.
    link.target = '_blank';
    link.download = item.document!.uploadedDocument!.fileName!;
    link.click();
  };

  const handleApprove = (item: ActivityCertificateModalForm) => async () => {
    await callWithLock(async () => {
      await invitationService.approveDocumentCertificate(invitation.id!, {
        entries: [item.document!.uploadedDocument!.id!],
      });
      await reloadInvitation();
    });
  };

  const handleDelete = (item: ActivityCertificateModalForm) => async () => {
    setIsSaving(true);
    await callWithLock(async () => {
      try {
        await mediaApi.unpairDocumentFromEntity(item.document.id!, 'FROM_INVITATION');
        await reloadInvitation();
      } catch (err) {
        setErrorSnacks(err, enqueueErrorSnackbar);
      } finally {
        setShowConfirmModal(false);
        setIsSaving(false);
      }
    });
  };

  return (
    <>
      <InvitedPersonsDocumentDateModal
        key={uploadModal?.document.requiredCertificate.id}
        open={dateModal}
        certificate={uploadModal?.document.requiredCertificate}
        onClose={handleDateModalClose}
      />
      <DocumentInputUploadModal file={selectedFile} onFileUploaded={handleFileUploaded} onCloseOnError={handleFileCancel} />
      <input type="file" ref={fileRef} onChange={({ target }) => handleFileChange(target.files)} hidden />

      <QcsModal open={!!item} onClose={onClose}>
        <QcsModalBox>
          <SectionHeader title="invitation.activity.certificateTitle" />
          <GridList<ActivityCertificateModalForm>
            headers={[
              { captionStr: 'invitation.activity.certificatePerson' },
              { captionStr: 'invitation.activity.certificateName' },
              { captionStr: 'invitation.activity.certificateValidTo' },
              {},
            ]}
            data={list}
            renderData={handleRenderData}
            search={false}
            hidePagination={true}
          />
        </QcsModalBox>
      </QcsModal>
    </>
  );
};
